import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Logo } from "../components/Logo";
import { CLIENT_INDEX, CLIENT_META } from "../lib/clientsData";

const ALPHABET = CLIENT_INDEX.map((g) => g.letter);

const ClientsHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="clients-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-white/10 py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8 flex items-center justify-between">
        <Logo />
        <Link
          to="/#gateways"
          data-testid="clients-back-explore-top"
          className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-neutral-300 hover:text-[#e62429] transition-colors duration-300"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          Explore
        </Link>
      </div>
    </header>
  );
};

export default function Clients() {
  const [active, setActive] = useState(null);
  const observer = useRef(null);

  useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.dataset.letter);
        });
      },
      { rootMargin: "-160px 0px -70% 0px", threshold: 0 }
    );
    const els = document.querySelectorAll("[data-letter-section]");
    els.forEach((el) => observer.current.observe(el));
    return () => observer.current && observer.current.disconnect();
  }, []);

  return (
    <div data-testid="clients-page" className="bg-[#0a0a0a] min-h-screen">
      <ClientsHeader />

      {/* Intro */}
      <section className="mx-auto max-w-[1500px] px-5 sm:px-8 pt-36 sm:pt-44 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#e62429] mb-6">
            <span className="h-px w-10 bg-[#e62429]" /> {CLIENT_META.label}
          </p>
          <h1 className="font-display uppercase text-white text-6xl sm:text-8xl lg:text-[8vw] leading-[0.85]">
            {CLIENT_META.title}
          </h1>
          <p className="mt-6 text-sm sm:text-base uppercase tracking-[0.18em] text-neutral-400 font-bold">
            {CLIENT_META.subtitle}
          </p>
        </motion.div>
      </section>

      {/* Sticky A–Z nav */}
      <nav
        data-testid="clients-az-nav"
        aria-label="Jump to letter"
        className="sticky top-[60px] sm:top-[68px] z-40 bg-[#0a0a0a]/90 backdrop-blur-xl border-y border-white/10"
      >
        <div className="mx-auto max-w-[1500px] px-3 sm:px-8">
          <ul className="flex items-center gap-0.5 sm:gap-1 overflow-x-auto py-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {CLIENT_INDEX.map((g) => {
              const empty = g.names.length === 0;
              const isActive = active === g.letter;
              return (
                <li key={g.letter} className="shrink-0">
                  {empty ? (
                    <span
                      data-testid={`az-${g.letter}-empty`}
                      aria-disabled="true"
                      className="flex h-9 w-9 items-center justify-center font-bebas text-lg text-neutral-700 cursor-default"
                      title="No entries"
                    >
                      {g.letter}
                    </span>
                  ) : (
                    <a
                      href={`#ltr-${g.letter}`}
                      data-testid={`az-${g.letter}`}
                      className={`flex h-9 w-9 items-center justify-center font-bebas text-lg transition-colors duration-200 ${
                        isActive
                          ? "bg-[#e62429] text-white"
                          : "text-neutral-400 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      {g.letter}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* Index */}
      <main className="mx-auto max-w-[1500px] px-5 sm:px-8 pb-24">
        {CLIENT_INDEX.map((g) => {
          const empty = g.names.length === 0;
          return (
            <section
              key={g.letter}
              id={`ltr-${g.letter}`}
              data-letter-section
              data-letter={g.letter}
              data-testid={`section-${g.letter}`}
              className="scroll-mt-[150px] grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 border-t border-white/10 py-10 sm:py-14"
            >
              <div className="md:col-span-2">
                <span
                  className={`font-display text-6xl sm:text-7xl leading-none ${
                    empty ? "text-neutral-800" : "text-white"
                  }`}
                >
                  {g.letter}
                </span>
              </div>
              <div className="md:col-span-10">
                {empty ? (
                  <p
                    data-testid={`empty-note-${g.letter}`}
                    className="text-neutral-600 text-lg"
                  >
                    —
                  </p>
                ) : (
                  <ul className="columns-1 sm:columns-2 lg:columns-3 gap-x-10">
                    {g.names.map((name, i) => (
                      <li
                        key={`${g.letter}-${i}`}
                        className="break-inside-avoid py-2 border-b border-white/[0.06] text-base sm:text-lg text-neutral-300 hover:text-white transition-colors duration-200"
                      >
                        {name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          );
        })}

        {/* Closing record */}
        <div className="border-t border-white/10 pt-12 mt-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p
            data-testid="clients-record"
            className="font-display uppercase text-2xl sm:text-3xl text-neutral-500"
          >
            {CLIENT_META.record}
          </p>
          <Link
            to="/#gateways"
            data-testid="clients-back-explore-bottom"
            className="group inline-flex items-center gap-3 border border-white/20 px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#e62429] hover:border-[#e62429] transition-colors duration-300"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to Explore
          </Link>
        </div>
      </main>
    </div>
  );
}
